import React, { useState, useEffect } from 'react';
import StarRating from './StarRating';
import ImageGallery from './ImageGallery';

const Sidebar = ({ selectedMarkerId, markers, onClose, onSubmitReview }) => {
  const [newReview, setNewReview] = useState({
    comment: '',
    rating: 0
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!markers || markers.length === 0) {
    return (
      <div className="sidebar-loading">
        <p>Markers are loading or not available...</p>
      </div>
    );
  }

  const selectedMarkerData = markers.find(marker => marker.id === selectedMarkerId);

  const handleSubmitReview = (e) => {
    e.preventDefault();
    if (newReview.rating === 0) {
      alert('Please select a rating');
      return;
    }
    onSubmitReview(selectedMarkerId, newReview);
    setNewReview({ comment: '', rating: 0 });
  };

  const calculateAverageRating = (reviews) => {
    if (!reviews) return 0;
    const reviewsArray = Object.values(reviews);
    if (reviewsArray.length === 0) return 0;
    const sum = reviewsArray.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviewsArray.length).toFixed(1);
  };

  return (
    <div className="sidebar" style={{
      width: isMobile ? '100%' : '400px',
      backgroundColor: '#ffffff',
      boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '12px' : '24px',
      position: 'fixed',
      top: '0',
      right: '0',
      bottom: '0',
      left: isMobile ? '0' : 'auto',
      overflowY: 'auto',
      display: selectedMarkerId ? 'block' : 'none',
      transition: 'all 0.3s ease',
      zIndex: 1000,
      transform: isMobile && !selectedMarkerId ? 'translateX(100%)' : 'translateX(0)',
      WebkitOverflowScrolling: 'touch',
      boxSizing: 'border-box'
    }}>
      <button 
        onClick={onClose}
        style={{
          position: 'absolute',
          right: isMobile ? '12px' : '24px',
          top: isMobile ? '12px' : '24px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
          zIndex: 1001
        }}
      >
        <span style={{ fontSize: '24px', color: '#666', lineHeight: '1' }}>×</span>
      </button>

      {selectedMarkerData ? (
        <div style={{ 
          marginTop: isMobile ? '40px' : '20px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          paddingRight: isMobile ? '8px' : '0'
        }}>
          {/* Header Section */}
          <h2 style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '16px',
            paddingRight: isMobile ? '24px' : '0'
          }}>
            {selectedMarkerData.name}
          </h2>

          {/* Replace the existing Image Gallery with the new component */}
          <ImageGallery washroomId={selectedMarkerId} />

          {/* Description Section */}
          <div style={{
            padding: isMobile ? '12px' : '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '24px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#666',
              margin: '0 0 16px 0'
            }}>
              {selectedMarkerData.description}
            </p>
            
            <a 
              href={selectedMarkerData.map}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                backgroundColor: '#ffffff',
                border: '1px solid #ddd',
                borderRadius: '20px',
                color: '#666',
                textDecoration: 'none',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = '#ddd';
              }}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                <circle cx="12" cy="9" r="2.5"/>
              </svg>
              Open in Google Maps
            </a>
          </div>

          {/* Reviews Section */}
          <div style={{ 
            marginBottom: '24px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '500',
              color: '#333',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              Reviews 
              <span style={{ 
                backgroundColor: '#f0f0f0',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '14px'
              }}>
                {calculateAverageRating(selectedMarkerData.reviews)} ★
              </span>
            </h3>

            {/* Add Review Form - Moved to top */}
            <form onSubmit={handleSubmitReview} style={{
              backgroundColor: '#f8f9fa',
              padding: isMobile ? '12px' : '16px',
              borderRadius: '8px',
              marginBottom: '24px'  // Added margin to separate from review list
            }}>
              <h4 style={{ margin: '0 0 16px 0' }}>Write a Review</h4>
              
              <div style={{ marginBottom: '16px' }}>
                <StarRating
                  rating={newReview.rating}
                  onRatingChange={(rating) => setNewReview(prev => ({ ...prev, rating }))}
                />
              </div>

              <textarea
                value={newReview.comment}
                onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Share your experience..."
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  marginBottom: '16px',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />

              <button
                type="submit"
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
              >
                Submit Review
              </button>
            </form>

            {/* Review List - Moved below */}
            <div>
              {selectedMarkerData.reviews && Object.entries(selectedMarkerData.reviews).map(([reviewId, review]) => (
                <div key={reviewId} style={{
                  padding: isMobile ? '12px' : '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontWeight: '500' }}>{review.user}</span>
                    <span style={{ color: '#ffd700' }}>
                      {'★'.repeat(review.rating)}{'☆'.repeat(5-review.rating)}
                    </span>
                  </div>
                  <p style={{ margin: '0', color: '#666' }}>{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p style={{
          textAlign: 'center',
          color: '#666',
          marginTop: '40px'
        }}>
          No marker selected
        </p>
      )}
    </div>
  );
};

export default Sidebar;
